import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { BUDGETS, MOBILITY_NEEDS, PROJECT_TYPES, TIMELINES } from '../data/content';
import { getReferralSource, submitLead } from '../lib/lead';
import { track } from '../lib/analytics';
import { useSettings } from '../lib/settings';
import { AddressField, emptyAddress, type AddressValue } from './ui/AddressField';
import { CustomSelect } from './ui/CustomSelect';
import { DateField } from './ui/DateField';
import { Icons, type IconName } from './ui/Icons';
import { Modal } from './ui/Modal';
import { emptyPhone, PhoneField, type PhoneValue } from './ui/PhoneField';

const RELATIONSHIPS = [
  { value: 'mother', label: 'My mother' },
  { value: 'father', label: 'My father' },
  { value: 'parent', label: 'My parent' },
  { value: 'spouse', label: 'My spouse or partner' },
  { value: 'myself', label: 'Myself' },
  { value: 'other', label: 'Someone else I care for' },
];

type Data = {
  address: AddressValue;
  relationship: string | null;
  project: string | null;
  mobility: string | null;
  timeline: string | null;
  budget: string | null;
  preferredDate: string | null;
  name: string;
  phone: PhoneValue;
  email: string;
};

const initialData = (): Data => ({
  address: emptyAddress(),
  relationship: null,
  project: null,
  mobility: null,
  timeline: null,
  budget: null,
  preferredDate: null,
  name: '',
  phone: emptyPhone(),
  email: '',
});

const STEP_TITLES = ['The home', 'The project', 'The situation', 'The details', 'You'];

// Reusable pick-card grid used across steps
function CardGrid({
  options,
  value,
  onPick,
  columns = 2,
}: {
  options: { id: string; label: string; sub?: string; icon?: IconName }[];
  value: string | null;
  onPick: (id: string) => void;
  columns?: number;
}) {
  return (
    <div className={`grid gap-3 ${columns === 2 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
      {options.map((o) => {
        const active = value === o.id;
        const Icon = o.icon ? Icons[o.icon] : null;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onPick(o.id)}
            className={`group flex cursor-pointer items-start gap-3 rounded-2xl border p-4 text-left transition-all duration-200 ${
              active ? 'border-terracotta bg-terracotta/[0.06] shadow-glow' : 'border-stone bg-white hover:border-terracotta/50 hover:bg-mist'
            }`}
          >
            {Icon && (
              <span
                className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl transition-colors ${
                  active ? 'bg-terracotta text-blush' : 'bg-mist text-terracotta group-hover:bg-terracotta/10'
                }`}
              >
                <Icon width={20} height={20} />
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block font-body text-[15px] font-semibold text-plum">{o.label}</span>
              {o.sub && <span className="mt-0.5 block font-body text-[13px] leading-snug text-plum/60">{o.sub}</span>}
            </span>
            <span
              className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border transition-all ${
                active ? 'border-terracotta bg-terracotta text-blush' : 'border-stone'
              }`}
            >
              {active && <Icons.check width={12} height={12} />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function LeadForm() {
  const { leadOpen, closeLeadForm, prefill, toast } = useApp();
  const settings = useSettings();
  const [step, setStep] = useState(0);
  const [data, setData] = useState<Data>(initialData);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ reference: string } | null>(null);
  const [attempted, setAttempted] = useState(false);

  // Apply prefill (zip/project) when opened
  useEffect(() => {
    if (leadOpen) {
      setData((d) => ({
        ...initialData(),
        ...d,
        address: prefill.zip ? { ...emptyAddress(), zip: prefill.zip } : d.address,
        project: prefill.projectId ?? d.project,
      }));
      track('lead_form_open', { prefill_project: prefill.projectId ?? '', prefill_zip: prefill.zip ?? '' });
    } else {
      // reset after close animation
      const t = window.setTimeout(() => {
        setStep(0);
        setData(initialData());
        setDone(null);
        setAttempted(false);
        setSubmitting(false);
      }, 350);
      return () => window.clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadOpen]);

  const set = <K extends keyof Data>(k: K, v: Data[K]) => setData((d) => ({ ...d, [k]: v }));

  const stepValid = useMemo(() => {
    switch (step) {
      case 0:
        return Boolean((data.address.zip || data.address.formatted.trim().length > 3) && data.relationship);
      case 1:
        return Boolean(data.project);
      case 2:
        return Boolean(data.mobility && data.timeline);
      case 3:
        return Boolean(data.budget);
      case 4:
        return data.name.trim().length > 1 && data.phone.valid;
      default:
        return false;
    }
  }, [step, data]);

  const next = () => {
    if (!stepValid) {
      setAttempted(true);
      return;
    }
    setAttempted(false);
    if (step < 4) {
      setStep((s) => s + 1);
      track('lead_step_complete', { step: step + 1 });
    } else {
      handleSubmit();
    }
  };
  const back = () => {
    setAttempted(false);
    setStep((s) => Math.max(0, s - 1));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const projectLabel = PROJECT_TYPES.find((p) => p.id === data.project)?.label ?? '';
    const res = await submitLead({
      home_address: data.address.formatted,
      zip: data.address.zip,
      city: data.address.city,
      state: data.address.state,
      recipient_relationship: data.relationship ?? '',
      project_type: data.project ?? '',
      project_label: projectLabel,
      mobility_need: data.mobility ?? '',
      timeline: data.timeline ?? '',
      budget: data.budget ?? '',
      preferred_date: data.preferredDate,
      contact_name: data.name.trim(),
      phone: data.phone.e164,
      phone_display: `${data.phone.country.dial} ${data.phone.national}`,
      email: data.email.trim(),
      referral_source: getReferralSource(),
      page_path: window.location.pathname,
    });
    setSubmitting(false);
    if (res.ok) {
      track('lead_submitted', { project: data.project, timeline: data.timeline });
      setDone({ reference: res.reference });
    } else {
      toast({ kind: 'error', title: "We couldn't send that just now", body: 'Please try again in a moment. Nothing was lost.' });
    }
  };

  const copySummary = async () => {
    const projectLabel = PROJECT_TYPES.find((p) => p.id === data.project)?.label ?? '';
    const text = `Rosa safety assessment: ${done?.reference}\nFor: ${RELATIONSHIPS.find((r) => r.value === data.relationship)?.label}\nProject: ${projectLabel}\nLocation: ${data.address.formatted || data.address.zip}\nA CAPS-certified specialist will call to arrange a free in-home assessment.`;
    try {
      await navigator.clipboard.writeText(text);
      toast({ kind: 'success', title: 'Copied', body: 'Share these details with your family.' });
    } catch {
      toast({ kind: 'info', title: 'Summary ready', body: text.split('\n')[0] });
    }
  };

  const progress = done ? 100 : (step / 5) * 100;

  return (
    <Modal open={leadOpen} onClose={closeLeadForm} labelledBy="lead-form-title">
      {/* Progress + header */}
      {!done && (
        <div className="shrink-0 border-b border-stone bg-blush/80 px-5 pb-4 pt-5 backdrop-blur sm:px-8">
          <div className="flex items-center gap-3 pr-10">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-terracotta/10 font-display text-lg font-bold text-terracotta">
              R
            </span>
            <div className="min-w-0">
              <p id="lead-form-title" className="font-display text-lg font-semibold leading-tight text-plum">
                Get a free safety assessment
              </p>
              <p className="font-body text-xs text-plum/55">
                Step {step + 1} of 5 · {STEP_TITLES[step]}
              </p>
            </div>
          </div>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-stone">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-terracotta to-sage"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
        </div>
      )}

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-6 sm:px-8">
        {done ? (
          <SuccessView reference={done.reference} data={data} onCopy={copySummary} onClose={closeLeadForm} responseHours={settings.response_window_hours} />
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              {step === 0 && (
                <div className="space-y-6">
                  <StepIntro title="Where is the home?" sub="So we can match a specialist who works in that area. Nothing is shared with more than one." />
                  <div>
                    <label className="mb-1.5 block font-body text-sm font-semibold text-plum">Home address or ZIP</label>
                    <AddressField
                      value={data.address}
                      onChange={(v) => set('address', v)}
                      invalid={attempted && !data.address.zip && data.address.formatted.trim().length <= 3}
                    />
                    <p className="mt-1.5 font-body text-xs text-plum/50">Start typing and pick from the list, or just enter a ZIP code.</p>
                  </div>
                  <div>
                    <label className="mb-2 block font-body text-sm font-semibold text-plum">Who is this for?</label>
                    <CustomSelect
                      options={RELATIONSHIPS}
                      value={data.relationship}
                      onChange={(v) => set('relationship', v)}
                      placeholder="Choose who the home is for"
                      ariaLabel="Who is this for"
                    />
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-5">
                  <StepIntro title="What does the home need?" sub="Pick the closest fit. Your specialist will confirm everything at the assessment." />
                  <CardGrid
                    options={PROJECT_TYPES.map((p) => ({ id: p.id, label: p.label, sub: p.short, icon: p.icon as IconName }))}
                    value={data.project}
                    onPick={(id) => set('project', id)}
                  />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-7">
                  <div className="space-y-4">
                    <StepIntro title="How is their mobility right now?" sub="This helps the specialist come prepared for the right kind of visit." />
                    <CardGrid
                      options={MOBILITY_NEEDS.map((m) => ({ id: m.id, label: m.label, sub: m.note }))}
                      value={data.mobility}
                      onPick={(id) => set('mobility', id)}
                    />
                  </div>
                  <div className="space-y-4">
                    <p className="block font-display text-lg font-semibold text-plum">What's the timeline?</p>
                    <CardGrid
                      options={TIMELINES.map((t) => ({ id: t.id, label: t.label, sub: t.note }))}
                      value={data.timeline}
                      onPick={(id) => set('timeline', id)}
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-7">
                  <div className="space-y-4">
                    <StepIntro title="Any budget in mind?" sub="A rough range is fine. The in-home assessment and quote are free either way." />
                    <CardGrid
                      options={BUDGETS.map((b) => ({ id: b.id, label: b.label, sub: b.range }))}
                      value={data.budget}
                      onPick={(id) => set('budget', id)}
                      columns={1}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block font-body text-sm font-semibold text-plum">
                      Preferred date for the visit <span className="font-normal text-plum/45">(optional)</span>
                    </label>
                    <DateField value={data.preferredDate} onChange={(v) => set('preferredDate', v)} placeholder="Pick a preferred date" />
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-5">
                  <StepIntro title="Where should your specialist reach you?" sub="One call from one vetted specialist. No spam, and never sold to a crowd of companies." />
                  <div>
                    <label className="mb-1.5 block font-body text-sm font-semibold text-plum">Your name</label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-plum/40">
                        <Icons.user width={18} height={18} />
                      </span>
                      <input
                        value={data.name}
                        onChange={(e) => set('name', e.target.value)}
                        placeholder="e.g. Diane Morrison"
                        autoComplete="name"
                        className={`w-full rounded-2xl border bg-white py-3.5 pl-11 pr-4 font-body text-plum outline-none transition-colors placeholder:text-plum/40 ${
                          attempted && data.name.trim().length <= 1 ? 'border-terracotta shadow-glow' : 'border-stone focus:border-terracotta focus:shadow-glow'
                        }`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block font-body text-sm font-semibold text-plum">Best phone number</label>
                    <PhoneField value={data.phone} onChange={(v) => set('phone', v)} invalid={attempted && !data.phone.valid} />
                  </div>
                  <div>
                    <label className="mb-1.5 block font-body text-sm font-semibold text-plum">
                      Email <span className="font-normal text-plum/45">(optional, for your confirmation)</span>
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-plum/40">
                        <Icons.mail width={18} height={18} />
                      </span>
                      <input
                        type="email"
                        value={data.email}
                        onChange={(e) => set('email', e.target.value)}
                        placeholder="you@example.com"
                        autoComplete="email"
                        className="w-full rounded-2xl border border-stone bg-white py-3.5 pl-11 pr-4 font-body text-plum outline-none transition-colors placeholder:text-plum/40 focus:border-terracotta focus:shadow-glow"
                      />
                    </div>
                  </div>
                  <p className="flex items-start gap-2 rounded-2xl bg-sage/8 p-3 font-body text-xs leading-relaxed text-sage-deep">
                    <Icons.shield width={16} height={16} className="mt-0.5 shrink-0" />
                    Your details go to one CAPS-certified specialist, never sold to five companies. Free, no obligation.
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* Footer nav */}
      {!done && (
        <div className="shrink-0 border-t border-stone bg-blush/90 px-5 py-4 backdrop-blur sm:px-8">
          {attempted && !stepValid && (
            <p className="mb-2 text-center font-body text-xs text-terracotta-deep">Please complete the highlighted fields to continue.</p>
          )}
          <div className="flex items-center gap-3">
            {step > 0 && (
              <button type="button" onClick={back} className="btn-secondary flex-1 sm:flex-none">
                <Icons.arrowLeft width={18} height={18} /> Back
              </button>
            )}
            <button
              type="button"
              onClick={next}
              disabled={submitting}
              className={`btn-primary flex-1 ${!stepValid ? 'opacity-95' : ''} ${submitting ? 'pointer-events-none opacity-80' : ''}`}
            >
              {submitting ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-blush/40 border-t-blush" /> Matching your specialist…
                </>
              ) : step === 4 ? (
                <>
                  Get my free assessment <Icons.check width={18} height={18} />
                </>
              ) : (
                <>
                  Continue <Icons.arrowRight width={18} height={18} />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function StepIntro({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <h3 className="font-display text-xl font-semibold text-plum sm:text-2xl">{title}</h3>
      <p className="mt-1.5 font-body text-[15px] leading-relaxed text-plum/65">{sub}</p>
    </div>
  );
}

function SuccessView({
  reference,
  data,
  onCopy,
  onClose,
  responseHours,
}: {
  reference: string;
  data: Data;
  onCopy: () => void;
  onClose: () => void;
  responseHours: number;
}) {
  const projectLabel = PROJECT_TYPES.find((p) => p.id === data.project)?.label ?? '';
  return (
    <div className="py-2 text-center">
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        className="relative mx-auto mb-5 grid h-20 w-20 place-items-center rounded-full bg-sage/15 text-sage-deep"
      >
        <span className="absolute inset-0 animate-pulse-ring rounded-full bg-sage/30" />
        <Icons.check width={40} height={40} />
      </motion.div>
      <h3 className="font-display text-2xl font-bold text-plum sm:text-3xl">We're on it.</h3>
      <p className="mx-auto mt-2 max-w-md font-body text-[15px] leading-relaxed text-plum/70">
        Thank you{data.name ? `, ${data.name.split(' ')[0]}` : ''}. A CAPS-certified specialist will call to arrange your free in-home safety
        assessment, usually within {responseHours} hours. We'll take it from here.
      </p>

      <div className="mx-auto mt-6 max-w-md rounded-2xl border border-stone bg-white/70 p-5 text-left">
        <div className="flex items-center justify-between border-b border-stone pb-3">
          <span className="font-body text-sm text-plum/60">Your reference</span>
          <span className="font-mono text-sm font-semibold text-terracotta">{reference}</span>
        </div>
        <dl className="mt-3 space-y-2 font-body text-sm">
          {projectLabel && (
            <div className="flex justify-between gap-4">
              <dt className="text-plum/55">Project</dt>
              <dd className="text-right font-medium text-plum">{projectLabel}</dd>
            </div>
          )}
          <div className="flex justify-between gap-4">
            <dt className="text-plum/55">Location</dt>
            <dd className="text-right font-medium text-plum">{data.address.city ? `${data.address.city}, ${data.address.state}` : data.address.zip || '—'}</dd>
          </div>
          <div className="flex items-center justify-between gap-4">
            <dt className="text-plum/55">Specialist</dt>
            <dd className="chip-sage">
              <Icons.badge width={14} height={14} /> CAPS-certified
            </dd>
          </div>
        </dl>
      </div>

      <div className="mx-auto mt-5 flex max-w-md flex-col gap-2.5 sm:flex-row">
        <button type="button" onClick={onCopy} className="btn-secondary flex-1">
          <Icons.user width={18} height={18} /> Copy for family
        </button>
        <button type="button" onClick={onClose} className="btn-primary flex-1">
          Done <Icons.check width={18} height={18} />
        </button>
      </div>
      <p className="mt-4 font-body text-xs text-plum/45">You'll receive a confirmation shortly. No obligation, no cost to you, ever.</p>
    </div>
  );
}
